import LegalLayout from "@/pages/legal/LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      eyebrow="Legal"
      lastUpdated="February 1, 2026"
      testid="privacy-page"
    >
      <p>
        This Privacy Policy describes how <strong>Ministry Branch</strong> (<strong>"we,"</strong>{" "}
        <strong>"our,"</strong> or <strong>"us"</strong>) collects, uses, and shares personal information in
        connection with the ChurchFlow web application and related services (the <strong>"Service"</strong>).
        ChurchFlow is part of the Ministry Branch family of services. Ministry Branch is headquartered in{" "}
        <strong>Fresno, California, USA</strong>.
      </p>
      <p>
        California residents: please also see our <a href="/legal/ccpa">CCPA / CPRA Rights notice</a> for additional
        disclosures and rights under California law.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account information</strong> — name, email, password (hashed), organization name.</li>
        <li><strong>Organization data</strong> — church / nonprofit profile, fiscal preferences, tax ID, addresses you choose to add.</li>
        <li><strong>Donor records</strong> — donor names, contact details, and giving history you enter.</li>
        <li><strong>Transactions</strong> — income, expense, fund, category, and notes you record.</li>
        <li><strong>Communications</strong> — emails or support requests you send to us.</li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li><strong>Device & log data</strong> — IP address, browser type, operating system, time zone, and pages visited.</li>
        <li><strong>Cookies & similar technologies</strong> — used for authentication (httpOnly session cookies) and to remember preferences.</li>
        <li><strong>Product analytics</strong> — aggregated usage signals to understand which features are used.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To provide, secure, and improve the Service;</li>
        <li>To authenticate your account and prevent unauthorized access;</li>
        <li>To generate the reports, statements, and exports you request;</li>
        <li>To respond to support requests and communicate updates;</li>
        <li>To comply with legal obligations and enforce our <a href="/legal/terms">Terms of Use</a>.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell personal information, and we do not use Customer Data (including donor
        records) to train third-party AI models.
      </p>

      <h2>3. How We Share Information</h2>
      <p>We share personal information only as described below:</p>
      <ul>
        <li><strong>Service providers</strong> — cloud hosting, database, email delivery, and analytics vendors who process data on our behalf under written contracts.</li>
        <li><strong>Legal compliance</strong> — when required by law, subpoena, or to protect the rights, safety, or property of Ministry Branch, our users, or the public.</li>
        <li><strong>Business transfers</strong> — in connection with a merger, acquisition, or sale of assets, subject to notice.</li>
        <li><strong>With your consent</strong> — for any other purpose disclosed to you and approved by you.</li>
      </ul>

      <h2>4. Data Retention</h2>
      <p>
        We retain Customer Data for as long as your account is active or as needed to provide the Service. If you
        delete your account, we will delete or de-identify Customer Data within 30 days, except where retention
        is required by law (e.g., financial records subject to IRS retention rules).
      </p>

      <h2>5. Security</h2>
      <p>
        We use industry-standard safeguards including TLS in transit, encryption at rest, bcrypt password
        hashing, role-based access controls, and audit logging. No system is perfectly secure; we encourage you
        to use a strong, unique password and protect your credentials.
      </p>

      <h2>6. Your Choices</h2>
      <ul>
        <li><strong>Access & correction</strong> — you can view and edit most data directly in the app.</li>
        <li><strong>Deletion</strong> — request account deletion at <a href="mailto:privacy@ministrybranch.com">privacy@ministrybranch.com</a>.</li>
        <li><strong>Marketing communications</strong> — unsubscribe via the link in any marketing email.</li>
        <li><strong>Cookies</strong> — most browsers let you block or delete cookies, though doing so may break authentication.</li>
      </ul>

      <h2>7. Children's Privacy</h2>
      <p>
        ChurchFlow is not directed to children under 13, and we do not knowingly collect personal information
        from them. If you believe a child has provided us personal information, contact us and we will delete it.
      </p>

      <h2>8. International Users</h2>
      <p>
        The Service is operated from the United States. If you access the Service from outside the U.S., your
        information will be transferred to and processed in the U.S., which may have different data-protection
        rules than your jurisdiction.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated by email or
        in-app notice at least 14 days before they take effect.
      </p>

      <h2>10. Contact</h2>
      <p>
        Ministry Branch · Fresno, California · USA<br />
        Privacy questions: <a href="mailto:privacy@ministrybranch.com">privacy@ministrybranch.com</a><br />
        General legal: <a href="mailto:legal@ministrybranch.com">legal@ministrybranch.com</a>
      </p>
    </LegalLayout>
  );
}
